// external imports
import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Chip, CircularProgress, Link } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { toast } from 'react-toastify';

// internal imports
import Layout from 'Component/Layouts/layout';
import useApiCaller from 'hooks/apiCaller';
import useCredentials from 'hooks/useCredentials';
import { useAssignments } from 'contexts/AssignmentsContext';
import { StatusPill, FileTypeBadge, formatDate, parseTaskError, downloadFile } from './common';
import SubmitTranslationModal from './SubmitTranslationModal';

// The per-assignment task page for a translator. Reached from My Assignments at
// /my-assignment/:taskType/:taskId. There's no by-assignment endpoint yet, so we
// load the task with GET /task/:taskId and pick out *this* translator's assignment.
const AssignmentDetail = () => {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const apiCaller = useApiCaller();
  const { userData } = useCredentials();
  const { refresh: refreshAssignments } = useAssignments();

  const [task, setTask] = React.useState(null);
  const [assignment, setAssignment] = React.useState(null); // this user's assignment
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [unauthorized, setUnauthorized] = React.useState(false);

  // submit modal state
  const [modalOpen, setModalOpen] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState('');

  const myId = userData?.employeeId;

  const fetchAssignment = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setUnauthorized(false);
      const res = await apiCaller.get(`/task/${taskId}`);
      const loadedTask = res?.data?.data?.task ?? null;
      const all = res?.data?.data?.assignments ?? [];
      const mine = all.find((a) => Number(a.assignedTo) === Number(myId)) || null;
      setTask(loadedTask);
      // attach the parent task so shared pieces (e.g. the submit modal) can read task.taskName
      setAssignment(mine ? { ...mine, task: loadedTask } : null);
    } catch (err) {
      const parsed = parseTaskError(err, 'Failed to load assignment. Please try again.');
      setError(parsed.message);
      setUnauthorized(parsed.unauthorized);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId, myId]);

  React.useEffect(() => {
    fetchAssignment();
  }, [fetchAssignment]);

  // POST /task/assignment/:id/submit — multipart, field "file"
  const handleSubmit = async (file) => {
    if (!assignment?._id) return;
    try {
      setSubmitting(true);
      setSubmitError('');
      const formData = new FormData();
      formData.append('file', file);
      await apiCaller.post(`/task/assignment/${assignment._id}/submit`, formData);
      toast.success(`${assignment.languageName} translation submitted.`);
      setModalOpen(false);
      await fetchAssignment(); // refresh this page
      refreshAssignments(); // refresh sidebar badge / My Assignments list
    } catch (err) {
      // 403 not-assigned / 400 already-approved / 400 cancelled / 400 file-required
      setSubmitError(
        parseTaskError(err, 'Failed to submit translation. Please try again.').message
      );
    } finally {
      setSubmitting(false);
    }
  };

  const sourceFiles = task?.attachments ?? task?.sourceFiles ?? [];
  const submission = assignment?.submission || {};
  const isCancelled = task?.status === 'cancelled';
  // backend allows (re)submit any time before approval; blocked once approved / cancelled
  const canSubmit = !!assignment && assignment.status !== 'approved' && !isCancelled;

  const submitLabel =
    assignment?.status === 'rejected'
      ? 'Re-upload Translation'
      : assignment?.status === 'submitted'
        ? 'Replace File'
        : 'Upload Translation';

  return (
    <Layout>
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          p: { xs: 2, md: 3 },
        }}
      >
        <Button
          startIcon={<ArrowBack sx={{ fontSize: 18 }} />}
          onClick={() => navigate('/my-assignments')}
          sx={{
            textTransform: 'none',
            color: '#64748b',
            mb: 1.5,
            '&:hover': { background: 'transparent', color: '#334155' },
          }}
        >
          Back to my assignments
        </Button>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress sx={{ color: '#6366f1' }} />
          </Box>
        ) : error ? (
          <Box
            sx={{
              background: '#fff',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              p: 4,
              textAlign: 'center',
            }}
          >
            <Typography
              sx={{ fontWeight: 700, color: unauthorized ? '#b45309' : '#b91c1c', mb: 0.5 }}
            >
              {unauthorized ? 'Not authorized' : 'Something went wrong'}
            </Typography>
            <Typography sx={{ color: unauthorized ? '#92400e' : '#dc2626', fontSize: 14 }}>
              {error}
            </Typography>
          </Box>
        ) : !assignment ? (
          <Box
            sx={{
              background: '#fff',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              p: 4,
              textAlign: 'center',
            }}
          >
            <Typography sx={{ fontWeight: 700, color: '#1e293b', mb: 0.5 }}>
              No assignment for you here
            </Typography>
            <Typography sx={{ color: '#64748b', fontSize: 14 }}>
              This task isn't assigned to you, so there's nothing to translate.
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              background: '#fff',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              p: { xs: 2.5, md: 3 },
            }}
          >
            {/* header: task + language + status */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 1.5,
                flexWrap: 'wrap',
              }}
            >
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
                  <Typography variant="h5" fontWeight="700" sx={{ color: '#1e293b' }}>
                    {task?.taskName || 'Task'}
                  </Typography>
                  <Chip
                    label={assignment.languageName}
                    size="small"
                    sx={{ background: '#eef2ff', color: '#4338ca', fontWeight: 600 }}
                  />
                </Box>
                <Typography sx={{ color: '#94a3b8', fontSize: 13, mt: 0.5 }}>
                  Assigned {formatDate(assignment.createdAt)}
                </Typography>
                {task?.taskDescription && (
                  <Typography sx={{ color: '#64748b', fontSize: 14, mt: 1, maxWidth: 720 }}>
                    {task.taskDescription}
                  </Typography>
                )}
              </Box>
              <StatusPill status={assignment.status} />
            </Box>

            {/* rejection remark */}
            {assignment.status === 'rejected' && assignment.reviewRemark && (
              <Box
                sx={{
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '10px',
                  px: 1.5,
                  py: 1,
                  mt: 2,
                }}
              >
                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#b91c1c', mb: 0.25 }}>
                  Sent back by the task manager
                </Typography>
                <Typography sx={{ fontSize: 13, color: '#b91c1c', fontStyle: 'italic' }}>
                  “{assignment.reviewRemark}”
                </Typography>
              </Box>
            )}

            {/* source files */}
            <Box sx={{ mt: 2.5 }}>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: '0.05em',
                  color: '#94a3b8',
                  mb: 1,
                }}
              >
                SOURCE FILE{sourceFiles.length > 1 ? 'S' : ''}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {sourceFiles.length === 0 && (
                  <Typography sx={{ fontSize: 13.5, color: '#94a3b8' }}>
                    No source files.
                  </Typography>
                )}
                {sourceFiles.map((file, i) => (
                  <Box
                    key={file.fileKey || file.fileName || i}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      border: '1px solid #e2e8f0',
                      borderRadius: '10px',
                      px: 1.25,
                      py: 0.75,
                    }}
                  >
                    <FileTypeBadge fileType={file.fileType} fileName={file.fileName} size={34} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: '#1e293b' }}>
                        {file.fileName}
                      </Typography>
                      {file.signedUrl && (
                        <Link
                          component="button"
                          type="button"
                          onClick={() => downloadFile(file.signedUrl, file.fileName)}
                          sx={{ fontSize: 12.5, fontWeight: 600, color: '#4f46e5' }}
                        >
                          Download
                        </Link>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* submission state + action */}
            <Box
              sx={{
                mt: 2.5,
                pt: 2,
                borderTop: '1px solid #f1f5f9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.5,
                flexWrap: 'wrap',
              }}
            >
              <Box>
                {assignment.status === 'submitted' && (
                  <Typography sx={{ fontSize: 13.5, color: '#475569' }}>
                    Submitted{submission.fileName ? ` — ${submission.fileName}` : ''} · awaiting
                    review
                    {submission.signedUrl && (
                      <>
                        {' '}
                        <Link
                          href={submission.signedUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ fontWeight: 600, color: '#4f46e5' }}
                        >
                          View
                        </Link>
                      </>
                    )}
                  </Typography>
                )}
                {assignment.status === 'approved' && (
                  <Typography sx={{ fontSize: 13.5, color: '#059669', fontWeight: 600 }}>
                    Approved — locked
                  </Typography>
                )}
                {isCancelled && (
                  <Typography sx={{ fontSize: 13.5, color: '#94a3b8' }}>
                    This task was cancelled.
                  </Typography>
                )}
              </Box>

              {canSubmit && (
                <Button
                  onClick={() => {
                    setSubmitError('');
                    setModalOpen(true);
                  }}
                  variant="contained"
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: '10px',
                    background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                    boxShadow: '0 4px 12px rgba(79, 70, 229, 0.25)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)',
                    },
                  }}
                >
                  {submitLabel}
                </Button>
              )}
            </Box>
          </Box>
        )}
      </Box>

      <SubmitTranslationModal
        open={modalOpen}
        assignment={assignment}
        submitting={submitting}
        error={submitError}
        onClose={() => (submitting ? null : setModalOpen(false))}
        onSubmit={handleSubmit}
      />
    </Layout>
  );
};

export default AssignmentDetail;
